import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Search, Heart, ShoppingBag, User, Gift, ChevronDown } from 'lucide-react';
import { useStore } from '@/context/StoreContext';
import { slugify } from '@/data/nav';
import api from '@/lib/api';
import { cx } from '@/lib/format';

const EMPTY_TAXO = { rooms: [], styles: [], materials: [], categories: [], festivals: [], collections: [] };

const FEATURE = {
  image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=600&q=80&auto=format&fit=crop',
  links: [
    { label: 'Complete Room Ideas', to: '/rooms' },
    { label: 'Curated Bundles', to: '/bundles' },
    { label: 'Inspiration Gallery', to: '/inspiration' },
    { label: 'Interior Designer Collection', to: `/collection/${slugify('Interior Designer Collection')}` },
  ],
};

const MegaPanel = ({ cfg }) => (
  <div className="grid grid-cols-4 gap-6 p-6">
    <div className="col-span-3">
      <p className="eyebrow mb-3">{cfg.title}</p>
      <div className={cx('grid gap-x-6 gap-y-1', cfg.cols === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
        {(cfg.items || []).map((it) => (
          <Link key={it.slug} to={`${cfg.base}/${it.slug}`} className="rounded-md px-2 py-1.5 text-sm text-foreground/80 transition hover:bg-[hsl(var(--gaze-ivory))] hover:text-[hsl(var(--primary))]">
            {it.name}
          </Link>
        ))}
      </div>
    </div>
    <div className="col-span-1">
      <div className="overflow-hidden rounded-xl">
        <img src={FEATURE.image} alt="Featured" className="h-32 w-full object-cover" />
      </div>
      <div className="mt-3 space-y-1.5">
        {FEATURE.links.map((l) => (
          <Link key={l.label} to={l.to} className="block text-sm font-medium text-[hsl(var(--primary))] hover:underline">{l.label}</Link>
        ))}
      </div>
    </div>
  </div>
);

export const Header = ({ onOpenMenu, onOpenSearch }) => {
  const { cartCount, wishlist, isAuthed } = useStore();
  const [openMenu, setOpenMenu] = useState(null);
  const [taxo, setTaxo] = useState(EMPTY_TAXO);
  const navigate = useNavigate();

  useEffect(() => { api.get('/taxonomy').then((r) => setTaxo(r.data)).catch(() => {}); }, []);

  const PANELS = {
    Rooms: { base: '/rooms', items: taxo.rooms, cols: 3, title: 'Shop by Room' },
    Styles: { base: '/style', items: taxo.styles, cols: 2, title: 'Shop by Style' },
    Materials: { base: '/material', items: taxo.materials, cols: 2, title: 'Shop by Material' },
    Shop: { base: '/category', items: taxo.categories, cols: 2, title: 'Categories' },
    Festival: { base: '/festival', items: taxo.festivals, cols: 2, title: 'Shop by Festival' },
    Collections: { base: '/collection', items: taxo.collections, cols: 2, title: 'Collections' },
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[hsl(var(--gaze-gold))]/25 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" data-testid="site-header">
      <div className="container-gaze flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onOpenMenu} className="md:hidden" aria-label="Open menu" data-testid="mobile-menu-open-button"><Menu size={22} /></button>
          <Link to="/" className="flex items-center gap-2 leading-none" data-testid="logo-link">
            <img src="/logo.png" alt="Gaze Decor" className="h-6 w-auto sm:h-9 sm:w-auto" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-xl tracking-wide sm:text-3xl">Gaze Decor</span>
              <span className="hidden text-[9px] uppercase tracking-[0.35em] text-[hsl(var(--primary-foreground))]/70 sm:block">Luxury Home Decor</span>
            </span>
          </Link>
        </div>

        <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-mega-menu" onMouseLeave={() => setOpenMenu(null)}>
          {Object.keys(PANELS).map((key) => (
            <div key={key} className="static" onMouseEnter={() => setOpenMenu(key)}>
              <button
                data-testid={`nav-${key.toLowerCase()}-trigger`}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))]/90 transition hover:text-[hsl(var(--gaze-gold))]"
              >
                {key} <ChevronDown size={14} className="opacity-70" />
              </button>
            </div>
          ))}
          <Link to="/gift-finder" data-testid="nav-gift-finder-link" className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-[hsl(var(--gaze-gold))]">
            <Gift size={15} /> Gift Finder
          </Link>
          {openMenu && (
            <div className="absolute inset-x-0 top-16 z-50" onMouseEnter={() => setOpenMenu(openMenu)}>
              <div className="container-gaze">
                <div className="mt-2 overflow-hidden rounded-2xl border border-border bg-[hsl(var(--background))] text-foreground shadow-[0_18px_50px_rgba(17,17,17,0.18)]">
                  <MegaPanel cfg={PANELS[openMenu]} />
                </div>
              </div>
            </div>
          )}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={onOpenSearch} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10" aria-label="Search" data-testid="search-open-button"><Search size={19} /></button>
          <Link to="/wishlist" className="relative hidden h-9 w-9 place-items-center rounded-full hover:bg-white/10 sm:grid" aria-label="Wishlist" data-testid="header-wishlist-link">
            <Heart size={19} />
            {wishlist.length > 0 && <span className="absolute right-0 top-0 grid h-4 w-4 place-items-center rounded-full bg-[hsl(var(--gaze-gold))] text-[9px] font-bold text-black">{wishlist.length}</span>}
          </Link>
          <button onClick={() => navigate(isAuthed ? '/account' : '/login')} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10" aria-label="Account" data-testid="header-account-link"><User size={19} /></button>
          <Link to="/cart" className="relative grid h-9 w-9 place-items-center rounded-full hover:bg-white/10" aria-label="Cart" data-testid="header-cart-link">
            <ShoppingBag size={19} />
            {cartCount > 0 && <span className="absolute right-0 top-0 grid h-4 w-4 place-items-center rounded-full bg-[hsl(var(--gaze-gold))] text-[9px] font-bold text-black">{cartCount}</span>}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;